import React, {useEffect, useState} from 'react';
import css from './Step.module.scss';
import {ButtonV2} from '../../../system/ButtonV2/ButtonV2';
import {WebMidiService} from '../WebMidiService';
import {Output} from 'webmidi';
import {useLocation} from 'react-router';
import {ScrollService} from '../ScrollService';
import {Spinner} from 'react-bootstrap';
import {faSync} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';

type OUTPUT_STATUS = 'searching' | 'found' | 'not_found' | undefined;
let DEVICE_NAMES = ['pulsoid', 'monitor'];

export const SetupMidiStep: React.FC<{
  midi: WebMidiService;
  scroller: ScrollService;
}> = ({midi, scroller}) => {
  const location = useLocation();

  const ref = React.useRef<HTMLDivElement>(null);

  const [midiStatus, setMidiState] = useState<
    'denied' | 'prompt' | 'granted'
  >();

  const [outputStatus, setOutputStatus] = useState<OUTPUT_STATUS>(undefined);
  const [output, setOutput] = useState<Output | undefined>(undefined);

  useEffect(() => {
    async function perform() {
      if (location.search.includes('midi-device-search')) {
        ref.current?.scrollIntoView({behavior: 'smooth'});
        await wait(1000);

        if (outputStatus === 'not_found') {
          setOutputStatus('searching');
          searchMidiDevice();
        }
      }
    }
    perform();
  }, [location]);

  useEffect(() => {
    async function perform() {
      let status = await midi.status();
      setMidiState(status);
      if (status === 'granted') {
        setOutputStatus('searching');
        searchMidiDevice();
      }
    }

    perform();
  }, []);

  let badge = '';
  let button = '';
  let action: () => Promise<void> = () => Promise.resolve();
  let inputName = output?.name || undefined;
  let badgeClass = css.badge_not_connected;
  let badgeSpinner: JSX.Element | null = null;

  let searchMidiDevice = async () => {
    await wait(3000);

    let outputs = await midi.refreshDevices();
    let pulsoidMidiDevice = outputs.find((input) => {
      let name = input.name.toLowerCase();
      return DEVICE_NAMES.some((deviceName) => name.includes(deviceName));
    });
    if (pulsoidMidiDevice) {
      setOutput(pulsoidMidiDevice);
      setOutputStatus('found');
      midi.setCurrentOutput(pulsoidMidiDevice);
    } else {
      setOutputStatus('not_found');
    }
  };

  switch (midiStatus) {
    case 'denied':
      badge = 'Blocked';
      button = 'How to unblock?';
      action = async () => {
        alert('How to unblock?');
      };
      break;
    case 'prompt':
      badge = 'Not enabled';
      button = 'Grant permission';
      action = async () => {
        setMidiState(await midi.enable());
      };
      break;
    case 'granted':
      switch (outputStatus) {
        case 'found':
          badge = 'Connected';
          badgeClass = css.badge_connected;
          break;
        case 'not_found':
          badge = 'Virtual MIDI device not found';
          button = 'Create Virtual MIDI device';
          action = async () => {
            scroller.scroll('midi-device-setup');
          };
          break;
        case 'searching':
          badge = 'Searching for MIDI device';
          badgeSpinner = <Spinner size="sm" animation="border" />;
          break;
        default:
          badge = 'Not connected';
          button = 'Search for MIDI device';
          action = searchMidiDevice;
      }

      break;
  }

  return (
    <div className={css.step + ' ' + css.first} ref={ref}>
      <div>Virtual MIDI device</div>
      <div className={css.badge}>
        <div className={badgeClass}>
          {badge} {badgeSpinner}
        </div>

        {outputStatus !== 'found' && midiStatus === 'granted' && (
          <ButtonV2
            type="secondary"
            size="small"
            disabled={outputStatus === 'searching'}
            onLoaderClick={async () => {
              setOutputStatus('searching');
              searchMidiDevice();
            }}
          >
            <FontAwesomeIcon size="lg" icon={faSync as IconDefinition} />
          </ButtonV2>
        )}
      </div>

      <div className={css.action}>
        {inputName && <div>{inputName}</div>}
        {!inputName && button && (
          <>
            <div>
              <ButtonV2
                // ref={buttonRef}
                type="primary-outline"
                size="small"
                onLoaderClick={action}
              >
                {button}
              </ButtonV2>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
